import { formatInTimeZone } from 'date-fns-tz'
import { Fragment, useMemo, useState, type ReactNode } from 'react'
import {
  formatCost,
  formatCount,
  formatLatency,
  MATCH_SIDE_LABELS,
  MATCH_VERDICT_LABELS,
  type AgentRunDetail,
  type AgentRunSummary,
  type AgentTranscript,
} from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import { pluralize } from '~/shared/lib/pluralize'
import { ACTORS, type Actor } from '../config'
import {
  agentMatchAgreement,
  buildAgentSteps,
  buildToolNameIndex,
  MATCH_SIDES,
  sessionLatency,
  type RunReview,
  type TimelineEffect,
  type TimelineEntry,
  type TimelineEvent,
  type TimelineSession,
} from '../lib'
import { AgentStepRow } from './agent-step-row'
import { AgreementBadge, MaterialIcon, Mono, Muted, StorySection } from './primitives'

function dayOf(ms: number): string {
  return formatInTimeZone(ms, 'UTC', 'EEEE, d MMMM yyyy')
}

function timeOf(ms: number | null): string {
  return ms === null ? '' : formatInTimeZone(ms, 'UTC', 'HH:mm')
}

function ActorMark({ actor }: { actor: Actor }) {
  const { icon, dot } = ACTORS[actor]
  return (
    <span
      className={cn(
        'ring-surface-lowest relative z-1 flex size-7 shrink-0 items-center justify-center rounded-full text-white ring-4',
        dot,
      )}
    >
      <MaterialIcon
        name={icon}
        className="text-[16px]"
      />
    </span>
  )
}

function Effect({ effect }: { effect: TimelineEffect }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-text-tertiary text-[12px] leading-4">{`${effect.label} →`}</span>
      {effect.value && (
        <span className="text-text-primary text-[13px] leading-5 font-medium">{effect.value}</span>
      )}
      {effect.codes &&
        (effect.codes.length ? (
          effect.codes.map((code) => (
            <span
              key={code}
              className="bg-surface-medium rounded-md px-1.5 font-mono text-[12px] leading-5"
            >
              {code}
            </span>
          ))
        ) : (
          <Muted>none</Muted>
        ))}
      {effect.agreement && effect.agreement !== 'none' && effect.agreement !== 'pending' && (
        <AgreementBadge
          agreement={effect.agreement}
          tip="Compared to the final state"
        />
      )}
    </div>
  )
}

function Row({
  time,
  actor,
  isLast,
  children,
}: {
  time: string
  actor: Actor
  isLast: boolean
  children: ReactNode
}) {
  return (
    <li className="relative grid grid-cols-[44px_28px_minmax(0,1fr)] gap-x-3">
      <span className="text-text-tertiary pt-1.5 text-right font-mono text-[11px] leading-4 tabular-nums">
        {time}
      </span>
      <div className="relative flex justify-center">
        {!isLast && <span className="bg-divider absolute top-7 bottom-[-4px] w-px" />}
        <ActorMark actor={actor} />
      </div>
      <div className="min-w-0 pb-5">{children}</div>
    </li>
  )
}

function EventBody({ event }: { event: TimelineEvent }) {
  return (
    <div className="flex flex-col gap-1 pt-0.5">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-text-primary text-[14px] leading-5 font-medium">{event.title}</span>
        {event.user && event.actor === 'reviewer' && <Muted>{event.user}</Muted>}
      </div>
      {event.detail && <Muted className="text-[13px] leading-5">{event.detail}</Muted>}
      {event.effect && <Effect effect={event.effect} />}
      {event.notes && (
        <span className="text-text-secondary text-[12px] leading-4 wrap-anywhere italic">{`“${event.notes}”`}</span>
      )}
    </div>
  )
}

function SessionOutcome({
  session,
  run,
  review,
}: {
  session: TimelineSession['session']
  run: AgentRunDetail
  review: RunReview
}) {
  if (session === 'classification') {
    if (!run.final) return <Muted>{`No result: the run ended as ${run.run_status}`}</Muted>
    const codes = run.final.tags?.tags?.map((tag) => tag.code) ?? []
    return (
      <div className="flex flex-col gap-1">
        <Effect
          effect={{
            label: 'Category',
            value: review.category.agent.label ?? 'None',
            agreement: review.category.agent.agreement,
          }}
        />
        <Effect effect={{ label: 'Codes', codes, agreement: review.codes.agent.agreement }} />
      </div>
    )
  }
  const match = run.match
  const picks = MATCH_SIDES.map((side) => ({ side, pick: match?.[side]?.pick })).filter(
    ({ pick }) => pick,
  )
  if (!picks.length) return <Muted>{`No pick: matching ${match?.status ?? 'did not run'}`}</Muted>
  return (
    <div className="flex flex-col gap-1">
      {picks.map(({ side, pick }) => {
        const scored = review.match?.side === side
        const verdict = scored ? review.match?.sideData?.verdict : null
        return (
          <div
            key={side}
            className="flex flex-wrap items-center gap-1.5"
          >
            <span className="text-text-tertiary text-[12px] leading-4">{`${MATCH_SIDE_LABELS[side]} case →`}</span>
            <span className="text-text-primary text-[13px] leading-5 font-medium capitalize">
              {pick!.decision}
            </span>
            {pick!.candidate && <Mono>{pick!.candidate}</Mono>}
            {pick!.confidence && <Muted>{`${pick!.confidence} confidence`}</Muted>}
            {verdict && (
              <AgreementBadge
                agreement={agentMatchAgreement(verdict)}
                label={MATCH_VERDICT_LABELS[verdict]?.label}
                tip={MATCH_VERDICT_LABELS[verdict]?.description}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function SessionBody({
  session,
  transcript,
  run,
  review,
}: {
  session: TimelineSession['session']
  transcript: AgentTranscript | null
  run: AgentRunDetail
  review: RunReview
}) {
  const steps = useMemo(() => (transcript ? buildAgentSteps(transcript) : []), [transcript])
  const toolNames = useMemo(() => buildToolNameIndex(transcript?.messages ?? []), [transcript])
  const [isOpen, setIsOpen] = useState(true)
  const isClassification = session === 'classification'
  const cost = isClassification
    ? (run.cost_usd ?? run.usage?.cost_usd_estimate)
    : run.match_cost_usd
  const calls = isClassification ? run.model_calls : run.match_model_calls
  const latency =
    (isClassification ? run.latency_ms : run.match_latency_ms) ?? sessionLatency(transcript)
  const rejected = steps.filter((step) => step.tone === 'error').length

  return (
    <div className="border-divider bg-surface-lowest flex flex-col rounded-xl border">
      <div className="flex flex-col gap-2 px-4 pt-3 pb-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <span className="text-text-primary text-[14px] leading-5 font-semibold">
            {isClassification
              ? 'The agent classified and tagged the document'
              : 'The agent matched the patient case'}
          </span>
          <Muted>
            {[
              `${steps.length} ${pluralize(steps.length, 'step')}`,
              formatLatency(latency),
              `${formatCount(calls)} model calls`,
              formatCost(cost),
              rejected
                ? `${rejected} ${pluralize(rejected, 'request')} rejected and retried`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Muted>
        </div>
        <SessionOutcome
          session={session}
          run={run}
          review={review}
        />
      </div>
      {transcript ? (
        <>
          <button
            type="button"
            onClick={() => setIsOpen((value) => !value)}
            className="border-divider text-text-secondary hover:text-text-primary flex cursor-pointer items-center gap-1 border-x-0 border-t border-b-0 bg-transparent px-4 py-2 text-left text-[12px] leading-4"
            aria-expanded={isOpen}
          >
            <MaterialIcon
              name={isOpen ? 'expand_less' : 'expand_more'}
              className="text-[18px]"
            />
            {isOpen
              ? 'Hide the steps'
              : `Show the ${steps.length} steps: what the agent asked for and what it got back`}
          </button>
          {isOpen && (
            <ol className="m-0 flex list-none flex-col px-2 pb-2">
              {steps.map((step) => (
                <AgentStepRow
                  key={step.id}
                  step={step}
                  toolNames={toolNames}
                />
              ))}
            </ol>
          )}
        </>
      ) : (
        <div className="border-divider border-t px-4 py-3">
          <Muted>
            {run.run_status.startsWith('skipped')
              ? 'No steps: the run was skipped before any model call.'
              : 'No transcript was stored for this session.'}
          </Muted>
        </div>
      )}
    </div>
  )
}

function OtherRun({ run, onSelect }: { run: AgentRunSummary; onSelect: (runId: string) => void }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 pt-1">
      <span className="text-text-secondary text-[13px] leading-5">Another agent run</span>
      <Muted>{[run.run_label, run.run_status].filter(Boolean).join(' · ')}</Muted>
      <button
        type="button"
        onClick={() => onSelect(run.run_id)}
        className="text-text-link cursor-pointer border-none bg-transparent p-0 text-[12px] leading-4 hover:underline"
      >
        View this run
      </button>
    </div>
  )
}

export function TimelineSection({
  entries,
  run,
  transcript,
  matchTranscript,
  review,
  onSelectRun,
  isHistoryLoading,
  historyError,
}: {
  entries: TimelineEntry[]
  run: AgentRunDetail
  transcript: AgentTranscript | null
  matchTranscript: AgentTranscript | null
  review: RunReview
  onSelectRun: (runId: string) => void
  isHistoryLoading: boolean
  historyError: boolean
}) {
  let lastDay: string | null = null

  return (
    <StorySection
      id="timeline"
      title="What happened"
      subtitle="The document's history, oldest first: what production, the agent and reviewers did, what each step asked for and got back, and how it changed the document. Times are UTC."
      aside={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {(Object.keys(ACTORS) as Actor[]).map((actor) => (
            <span
              key={actor}
              className="text-text-secondary inline-flex items-center gap-1.5 text-[12px] leading-4"
            >
              <span className={cn('size-2 rounded-full', ACTORS[actor].dot)} />
              {ACTORS[actor].label}
            </span>
          ))}
        </div>
      }
    >
      <div className="flex flex-col px-4 pt-4 pb-1">
        {isHistoryLoading && <Muted className="pb-3">Loading the document history…</Muted>}
        {historyError && (
          <Muted className="pb-3">
            {"The document's history could not be loaded; only the agent's sessions are shown."}
          </Muted>
        )}
        <ol className="m-0 list-none p-0">
          {entries.map((entry, index) => {
            const day = entry.at !== null ? dayOf(entry.at) : null
            const showDay = day !== null && day !== lastDay
            if (day) lastDay = day
            const isLast = index === entries.length - 1
            const actor: Actor = entry.kind === 'event' ? entry.actor : 'agent'
            return (
              <Fragment key={`${entry.kind}-${entry.id}`}>
                {showDay && (
                  <li className="text-text-secondary pt-1 pb-3 pl-[56px] text-[12px] leading-4 font-semibold tracking-[0.4px] uppercase">
                    {day}
                  </li>
                )}
                <Row
                  time={timeOf(entry.at)}
                  actor={actor}
                  isLast={isLast}
                >
                  {entry.kind === 'event' && <EventBody event={entry} />}
                  {entry.kind === 'session' && (
                    <SessionBody
                      session={entry.session}
                      transcript={entry.session === 'classification' ? transcript : matchTranscript}
                      run={run}
                      review={review}
                    />
                  )}
                  {entry.kind === 'other-run' && (
                    <OtherRun
                      run={entry.run}
                      onSelect={onSelectRun}
                    />
                  )}
                </Row>
              </Fragment>
            )
          })}
        </ol>
      </div>
    </StorySection>
  )
}
